import circle from '@turf/circle';
import intersect from '@turf/intersect';

import { DeliveryArea, DeliveryAreaValue, DeliveryPricing, Feature, GeoRange } from 'apis/delivery-settings-api/types';

import { getLabelPosition } from './mapUtils/mapUtils';

const hasDistancePricing = (deliveryPricing: DeliveryPricing | undefined) => {
  if (!deliveryPricing) {
    return false;
  }

  const { distanceBasedPricing } = deliveryPricing;
  if (distanceBasedPricing.length === 0) {
    return false;
  }

  const [firstDistanceRange] = distanceBasedPricing;
  if (distanceBasedPricing.length === 1 && Object.values(firstDistanceRange).every(v => !v)) {
    return false;
  }

  return true;
};

const getMinBasketSize = (deliveryPricing: DeliveryPricing | undefined): number | undefined => {
  if (!deliveryPricing) {
    return undefined;
  }

  const [firstBasketRange] = deliveryPricing.basketSizeBasedPricing;
  if (
    firstBasketRange &&
    (firstBasketRange.basketMultiplierDecimal < 0 || firstBasketRange.pricingFlag === 'not_available')
  ) {
    return firstBasketRange.rangeMaxBound;
  }

  return 0;
};

const circleSteps = 32;
const createCircleFeature = (center: [number, number], radiusInKm: number): Feature =>
  circle(center, radiusInKm, {
    steps: circleSteps,
    units: 'kilometers',
    properties: { radiusInKm },
  });

const getPricingCircles = (
  deliveryPricing: DeliveryPricing | undefined,
  area: GeoRange | undefined,
  center: [number, number] | undefined,
) => {
  if (!deliveryPricing || !area || !center || !hasDistancePricing(deliveryPricing)) {
    return undefined;
  }

  const steps = deliveryPricing.distanceBasedPricing;
  return steps.reduce<Feature[]>((results, step) => {
    const radius = step.rangeMaxBound;
    if (!radius) {
      return results;
    }

    const circle = createCircleFeature(center, radius / 1000);
    // Only include the parts of the circle that are inside the area
    try {
      const cutResult = intersect(circle, {
        type: 'Polygon',
        coordinates: area.coordinates.map(coords => coords.map(({ latitude, longitude }) => [longitude, latitude])),
      });
      if (cutResult) {
        results.push(cutResult);
      }
    } catch {
      // Sometimes the intersect function fails for not apparent reason
    }

    return results;
  }, []);
};

/** Parse and convert the delivery areas from api to more easy-to-use properties */
export const convertDeliveryAreas = (
  deliveryAreas: DeliveryArea[] | undefined,
  venueCoordinates: [number, number] | undefined,
): DeliveryAreaValue[] => {
  if (!deliveryAreas) {
    return [];
  }

  return deliveryAreas.map(({ id, name, deliveryPricing, geoRange, disabled }) => ({
    id,
    name,
    deliveryBaseFee: deliveryPricing?.basePrice,
    distanceBasedPricing: hasDistancePricing(deliveryPricing) ? deliveryPricing?.distanceBasedPricing : undefined,
    minBasketSize: getMinBasketSize(deliveryPricing),
    geoRange,
    labelPosition: geoRange ? getLabelPosition(geoRange) : undefined,
    pricingCircles: getPricingCircles(deliveryPricing, geoRange, venueCoordinates),
    disabled: !!disabled,
  }));
};
